import Reveal from '../components/animations/Reveal';
import { Building2, Globe, FlaskConical, Dna, BarChart3, Cpu } from 'lucide-react';

const sampleCollection = [
    'ONESpit™ – Saliva Collection and Preservation Kit (Patent No: 564910)',
    'ONEasy™ – Faecal Collection and Preservation Kit',
    'NucleoGUARD™ – RNA Stabilization Buffer',
    'RNAGUARD™ – Ambient Shipping of Total RNA',
    'ProteinGUARD™ – Ambient Shipping of Protein',
    'SoilGUARD™ – Soil Stabilization Buffer',
];

const extractionKits = [
    'ONEMag™ Rapid Plant DNA Extraction Kit',
    'ONEMag™ Rapid Soil DNA Extraction Kit',
    'ONEMag™ Rapid Universal DNA Extraction Kit (Blood, Serum, Tissue, Saliva, Sputum, Faecal, Urine, Swab, Bacteria and Fungi)',
    'ONEMag™ Rapid Plant RNA Extraction Kit',
    'ONEMag™ Rapid Soil RNA Extraction Kit',
];

const libraryPrepKits = [
    'ONENext™ 16S (V3-V4) Library Prep Kit for Illumina (96 rxns)',
    'ONENext™ 16S (V1-V9) Library Prep Kit for ONT (96 rxns)',
];

const sequencingServices = [
    'Oxford Nanopore Long Read Sequencing',
    'Whole Genome Sequencing (WGS)',
    'Whole Genome Bisulphite Sequencing (WGBS)',
    'ChIP-Sequencing',
    '16s rRNA / 18s rRNA / ITS – Metagenome Sequencing',
    'Shotgun Metagenome Sequencing',
    'Metatranscriptome Sequencing',
    'RNA Sequencing (mRNA)',
    'Whole Transcriptome Sequencing (WTS)',
    'Dual RNA Sequencing',
    'Small RNA / miRNA Sequencing',
    'Single-Cell RNA Sequencing',
    'Isoform Sequencing (Iso-Seq)',
    'Genotyping by Sequencing (GBS)',
    'Targeted Sequencing (Plants & Animals)',
    'Gene Expression Analysis (qRT-PCR)',
    'Metabarcoding',
    'Custom Bioinformatics Analysis',
    'Custom NGS Panel Development',
];

const clinicalServices = [
    'Whole Exome Sequencing (WES)',
    'Clinical Exome Sequencing (CES)',
    'Gut Microbiome',
    'Vaginal Microbiome',
    'Diabetic Foot Ulcer',
    'Rare Disease Genetic Testing',
    'Cancer Screening',
    'New Born Screening',
    'Karyotyping / FISH',
    'Non-Invasive Prenatal Testing (NIPT)',
    'Pre-Implantation Genetic Diagnosis (PGD)',
    'Biological Age Testing',
    "Alzheimer's Risk Testing",
    'HLA Typing',
    'Personal Genome Service (2800 Disorders + 600 Drug Response)',
    'Pharmacogenomics (PGx)',
];

const software = [
    'DNASTAR LASERGENE',
    'Cry Toxin Finder (Open Source)',
    'myomics.ai (SaaS based platform for Rare Disease Diagnosis)',
];

const ServiceSection = ({ title, items, delay = 0 }) => (
    <Reveal delay={delay}>
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:border-brand hover:shadow-md transition-all duration-300">
            <div className="bg-brand px-5 py-3">
                <h4 className="font-display text-sm font-bold text-white uppercase tracking-wider">{title}</h4>
            </div>
            <ul className="p-4 space-y-1.5">
                {items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-900">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand flex-shrink-0"></span>
                        {item}
                    </li>
                ))}
            </ul>
        </div>
    </Reveal>
);

const AboutOneomicsPage = () => (
    <div className="py-16 space-y-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Hero */}
            <Reveal>
                <div className="text-center mb-16 max-w-4xl mx-auto">
                    {/* <img
                        src="/MYOMICS.png"
                        alt="ONEOMICS"
                        className="h-16 mx-auto mb-6 object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                    /> */}
                    <h2 className="font-display text-4xl font-bold text-slate-900 mb-6">
                        About <span className="text-brand">ONEOMICS</span>
                    </h2>
                    <p className="text-xl text-slate-600 leading-relaxed">
                        ONEOMICS Private Limited is a genomics and bioinformatics company based in Tiruchirappalli,
                        Tamil Nadu, India — delivering cutting-edge Bio-IT solutions, genomic services, and
                        AI-driven platforms for life sciences and agriculture.
                    </p>
                </div>
            </Reveal>

            {/* Capability cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                {[
                    { icon: Dna, title: 'Genomics Services', text: 'Next-generation sequencing, 16S amplicon, mRNA, WGS, and ONT long-read services.' },
                    { icon: Cpu, title: 'Bioinformatics & AI', text: 'Custom pipelines, AI-driven discovery tools, and cloud-based analysis platforms.' },
                    { icon: FlaskConical, title: 'Clinical Diagnostics', text: 'Molecular diagnostics solutions for clinical and research laboratories.' },
                    { icon: BarChart3, title: 'Lab Management', text: 'Delphina LIMS — a comprehensive laboratory information management system.' },
                    { icon: Building2, title: 'Enterprise Software', text: 'PanteERP — an ERP platform built for genomics and life science businesses.' },
                    { icon: Globe, title: 'Agricultural Biotech', text: 'CryMiner and agricultural bioinformatics tools for sustainable pest management.' },
                ].map(({ icon: Icon, title, text }, i) => (
                    <Reveal key={i} delay={i * 80}>
                        <div className="h-full bg-white rounded-2xl p-6 border border-slate-200 hover:border-brand hover:-translate-y-2 hover:shadow-lg transition-all duration-300 group">
                            <div className="inline-flex w-12 h-12 rounded-xl items-center justify-center bg-brand-tint mb-4">
                                <Icon className="w-7 h-7 text-brand group-hover:scale-110 group-hover:rotate-6 transition-all duration-300" />
                            </div>
                            <h4 className="font-display text-lg font-semibold text-slate-900 mb-2">{title}</h4>
                            <p className="text-slate-600 text-sm">{text}</p>
                        </div>
                    </Reveal>
                ))}
            </div>

            {/* Partner logos — above products */}
            <Reveal>
                <div className="mb-16">
                      <Reveal>
                <h3 className="font-display text-2xl font-bold text-slate-900 mb-8 text-center">
                    Our  <span className="text-brand">Platforms</span>
                </h3>
            </Reveal>
                    <div className="flex flex-wrap justify-center items-center gap-8">
                        {[
                            { src: '/logo.png', alt: 'ONEOMICS Logo' },
                            { src: '/myGUTBIOME.png', alt: 'myGutBiome' },
                            { src: '/MYNIPT.png', alt: 'myNIPT' },
                            { src: '/MYOMICS.png', alt: 'ONEOMICS Logo' },

                            { src: '/Companylogo.png', alt: 'Company Logo' },
                            { src: '/accuAGE.png', alt: 'accuAGE' },
                        ].map(({ src, alt }) => (
                            <img
                                key={src}
                                src={src}
                                alt={alt}
                                className="h-12 object-contain"
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        ))}
                    </div>
                </div>
            </Reveal>

            {/* Products & Services */}
            <Reveal>
                <h3 className="font-display text-2xl font-bold text-slate-900 mb-8 text-center">
                    Products &amp; <span className="text-brand">Services</span>
                </h3>
            </Reveal>

            {/* Kits — 3 columns */}
            <div className="grid md:grid-cols-3 gap-6 mb-6">
                <ServiceSection title="Sample Collection & Shipping" items={sampleCollection} delay={0} />
                <ServiceSection title="Extraction Kits" items={extractionKits} delay={80} />
                <ServiceSection title="Library Prep Kits" items={libraryPrepKits} delay={160} />
            </div>

            {/* Services + Clinical — 2 columns */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
                <ServiceSection title="Sequencing Services" items={sequencingServices} delay={0} />
                <ServiceSection title="Clinical Services" items={clinicalServices} delay={80} />
            </div>

            {/* Software — full width */}
            <div className="mb-16">
                <ServiceSection title="Software" items={software} delay={0} />
            </div>


        </div>
    </div>
);

export default AboutOneomicsPage;